import React, {FC, useState, useEffect, useRef, useMemo} from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  IconButton,
  TextField,
} from '@mui/material';
import AurSearchBox from '../../common/aur-search-box/AurSearchBox';

import {ReactComponent as ArrowRightOutlinedIcon} from '../../../assests/svg/right-icon-large.svg';
import {ReactComponent as ArrowRightOutlinedIconActive} from '../../../assests/svg/right-icon-large-active.svg';
import {ReactComponent as ArrowDoubbleRight} from '../../../assests/svg/double-right-icon.svg';
import {ReactComponent as ArrowDoubbleRightInactive} from '../../../assests/svg/double-icon-right-inactive.svg';
import {ReactComponent as ArrowLeftOutlinedIcon} from '../../../assests/svg/left-icon-large.svg';
import {ReactComponent as ArrowLeftOutlinedIconActive} from '../../../assests/svg/left-icon-large-active.svg';
import {ReactComponent as ArrowDoubbleLeft} from '../../../assests/svg/double-left-icon.svg';
import {ReactComponent as ArrowDoubbleLeftActive} from '../../../assests/svg/double-left-active.svg';
import {ReactComponent as CheckedIcon} from '../../../assests/svg/checkmark-blue.svg';
import {ReactComponent as FilterList} from '../../../assests/svg/filter-list.svg';
import {useDispatch, useSelector} from 'react-redux';
import {t} from 'i18next';
import AurButton from '../../common/aur-button/AurButton';
import AurPopup from '../../common/aur-popup/AurPopup';
import {useNavigate} from 'react-router-dom';
import {RootState} from '../../../redux/store';
import {ReactSortable} from 'react-sortablejs'; // Importing the correct component
import {ReactComponent as DragAndDrop} from '../../../assests/svg/drag-and-drop.svg';
import {creatExports} from '../../../api/EcaServices';
import AurToast from '../../common/aur-toast/AurToast';
import {
  setSelectedExport,
  setSelectedFieldsData,
} from '../../../redux/slices/addExportSlice';
import {FieldData} from './CreateExportTypes';
import {
  IExportState,
  ExportForAnalytics,
  ExportForReview,
} from './export-step-2/ExportStates';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

type CreateExportFormStep2Props = {
  fieldData: FieldData[];
  radioBtnType: string;
};

const CreateExportStep2: FC<CreateExportFormStep2Props> = (props) => {
  const {fieldData, radioBtnType} = props;

  const [exportState, setExportState] = useState<IExportState>();
  const [availableFields, setAvailableFields] = useState<FieldData[]>([]);
  const [selectedFields, setSelectedFields] = useState<FieldData[]>([]);
  const [selectedAvailable, setSelectedAvailable] = useState<FieldData[]>([]); // Store full field objects
  const [selectedSelected, setSelectedSelected] = useState<FieldData[]>([]); // Store full field objects
  const [searchTermAvailable, setSearchTermAvailable] = useState('');
  const [searchTermSelected, setSearchTermSelected] = useState('');
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [openErrorPopup, setOpenErrorPopup] = useState<boolean>(false);
  const [createButtonState, setCreateButtonState] = useState<boolean>(false);
  const [apiErrorMsg, setApiErrorMsg] = useState<string>('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();

  const {clientId, projectId} = useSelector(
    (state: RootState) => state.reports
  );

  const step2SelectedField = useSelector(
    (state: RootState) => state.addExport?.selectedFields
  );

  useEffect(() => {
    let exportState: IExportState;
    if (radioBtnType === 'export_for_analytics') {
      // Export for Analytics
      exportState = new ExportForAnalytics(fieldData);
    } else {
      // Export for Review
      exportState = new ExportForReview(fieldData);
    }

    setExportState(exportState);
    setAvailableFields(exportState.fields);
  }, [radioBtnType, fieldData]);

  const filterOptions = [
    {id: 1, display: t('ecaDocList.filterOptions.allFields'), fieldType: 'All'},
    {
      id: 2,
      display: t('ecaDocList.filterOptions.standardMetadataFields'),
      fieldType: 'Standard Metadata',
    },
    {
      id: 3,
      display: t('ecaDocList.filterOptions.customMetadataFields'),
      fieldType: 'Custom Metadata',
    },
    {id: 4, display: t('ecaDocList.filterOptions.strFields'), fieldType: 'STR'},
    {id: 5, display: t('ecaDocList.filterOptions.tagFields'), fieldType: 'Tag'},
  ];

  const defaultSelectedFields = useMemo(() => {
    return fieldData.filter((field: FieldData) =>
      step2SelectedField.includes(field.fieldLabel)
    );
  }, [fieldData]);

  useEffect(() => {
    if (defaultSelectedFields.length > 0) {
      dispatch(setSelectedFieldsData(defaultSelectedFields));
    }

    if (fieldData.length > 0 && radioBtnType === 'export_for_analytics') {
      // Set selected fields
      setSelectedFields(
        defaultSelectedFields.map((value: any) => ({
          ...value,
          chosen: false,
        }))
      );

      // Set available fields by excluding selected ones
      const remaining = fieldData.filter(
        (field: FieldData) =>
          !defaultSelectedFields.some(
            (sel: FieldData) => sel.fieldId === field.fieldId
          )
      );
      setAvailableFields(remaining);
    }
  }, [fieldData]);

  const trackAddOrRemove = (selectedFields: FieldData[], isAdd: boolean) => {
    let tagFieldSelecteCount = 0;
    let customfieldsSelectedCount = 0;
    const standeredFieldsSelected: FieldData[] = [];
    selectedFields.map((field) => {
      if (field?.fieldCategoryLabel) {
        switch (field.fieldCategoryLabel.toLowerCase()) {
          case 'tag':
            tagFieldSelecteCount++;
            break;
          case 'custom metadata':
            customfieldsSelectedCount++;
            break;
          default:
            standeredFieldsSelected.push(field);
        }
      }
    });
    if (tagFieldSelecteCount > 0) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: isAdd
            ? t('gaTracking.export.addFields')
            : t('gaTracking.export.removeFields'),
          type: GA_CLICK_TYPES.ICON,
          region: GA_REGIONS.TAB,
          action: t('gaTracking.export.fieldsSelected'),
          details: t('gaTracking.export.tag', {count: tagFieldSelecteCount}),
        },
        subPage: {
          subpage_name: t('gaTracking.export.selectFields'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    }
    if (customfieldsSelectedCount > 0) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: isAdd
            ? t('gaTracking.export.addFields')
            : t('gaTracking.export.removeFields'),
          type: GA_CLICK_TYPES.ICON,
          region: GA_REGIONS.TAB,
          action: t('gaTracking.export.fieldsSelected'),
          details: t('gaTracking.export.customMetadata', {
            count: customfieldsSelectedCount,
          }),
        },
        subPage: {
          subpage_name: t('gaTracking.export.selectFields'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    }
    standeredFieldsSelected.map((field) => {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: isAdd
            ? t('gaTracking.export.addFields')
            : t('gaTracking.export.removeFields'),
          type: GA_CLICK_TYPES.ICON,
          region: GA_REGIONS.TAB,
          action: t('gaTracking.export.fieldsSelected'),
          details: t('gaTracking.export.standardMetadata', {
            data: field.fieldLabel,
          }),
        },
        subPage: {
          subpage_name: t('gaTracking.export.selectFields'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    });
  };

  const moveToSelected = () => {
    trackAddOrRemove(selectedAvailable, true);

    // Step 1: Update selected fields
    setSelectedFields((prevSelectedFields) => [
      ...prevSelectedFields,
      ...selectedAvailable,
    ]);

    // Step 2: Remove selected fields from available fields
    setAvailableFields((prevAvailableFields) =>
      prevAvailableFields.filter((field) => !selectedAvailable.includes(field))
    );

    // Step 3: Update displayedAvailableFields to remove the selected fields
    setDisplayedAvailableFields((prevDisplayedFields) =>
      prevDisplayedFields.filter((field) => !selectedAvailable.includes(field))
    );

    // Step 4: Reset selectedAvailable
    setSelectedAvailable([]);
  };

  const moveToAvailable = () => {
    trackAddOrRemove(selectedSelected, false);
    setAvailableFields((prevAvailableFields) => [
      ...prevAvailableFields,
      ...selectedSelected,
    ]);

    setSelectedFields((prevSelectedFields) =>
      prevSelectedFields.filter((field) => !selectedSelected.includes(field))
    );

    setSelectedSelected([]);

    // Update displayedAvailableFields to include the fields moved back to available
    setDisplayedAvailableFields((prevDisplayedFields) => [
      ...prevDisplayedFields,
      ...selectedSelected,
    ]);

    //Reapply the filtering for displayedAvailableFields after move
    applyFilteringAndSorting(availableFields, searchTermAvailable);
  };

  const applyFilteringAndSorting = (
    fields: FieldData[],
    searchTerm: string
  ) => {
    if (searchTerm) {
      const filteredFields = fields
        .filter((field) =>
          field.fieldLabel.toLowerCase().includes(searchTerm.toLowerCase())
        ) // Apply label search
        .sort((a, b) => a.fieldLabel.localeCompare(b.fieldLabel)); // Sorting by label
      setDisplayedAvailableFields(filteredFields); // Apply filtered fields to displayedAvailableFields
    }
  };

  const handleToggleSelected = (field: any) => {
    setSelectedSelected((prev) =>
      prev.includes(field)
        ? prev.filter((item) => item !== field)
        : [...prev, field]
    );
  };

  const handleToggleAvailable = (field: any) => {
    setSelectedAvailable((prev) =>
      prev.includes(field)
        ? prev.filter((item) => item !== field)
        : [...prev, field]
    );
  };

  // Debounce search term to avoid excessive re-renders
  // eslint-disable-next-line @typescript-eslint/ban-types
  const debounce = (fn: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), delay);
    };
  };

  const handleSearchChange = debounce(
    (search: string, type: 'available' | 'selected') => {
      if (type === 'available') {
        setSearchTermAvailable(search);
      } else {
        setSearchTermSelected(search);
      }
    },
    100
  );

  const isFieldSelected = (field: any, selectedList: any[]) =>
    selectedList.includes(field);

  const [open, setOpen] = useState(false); // Dropdown open/close state
  const [selectedOption, setSelectedOption] = useState<any>(1); // State to track selected option
  const menuRef = useRef<HTMLDivElement | null>(null); // Reference to the dropdown menu
  const buttonRef = useRef<HTMLButtonElement | null>(null); // Reference to the button
  const [openEmailPopup, setOpenEmailPopup] = useState<boolean>(false);
  const [displayedAvailableFields, setDisplayedAvailableFields] = useState<
    any[]
  >([]);

  const [displayedSelectedFields, setDisplayedSelectedFields] = useState<any[]>(
    []
  );
  const [emails, setEmails] = useState('');
  const [error, setError] = useState('');
  const [isButtonDisabled, setisButtonDisabled] = useState<boolean>(true);
  const [createExportConfirm, setCreateExportConfirm] =
    useState<boolean>(false);

  // Close dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false); // Close the dropdown if clicking outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemSelect = (item: any) => {
    setSearchTermAvailable('');
    setSelectedOption(item.id); // Update selected option
  };

  useEffect(() => {
    // Filter by fieldType (if any) based on selectedOption
    let filteredFields: FieldData[];
    if (selectedOption === 1) {
      // All fields
      filteredFields = availableFields;
    } else {
      // Specific field type
      const fieldType = filterOptions.find(
        (i) => i.id === selectedOption
      )?.fieldType;
      filteredFields = availableFields.filter(
        (field) => field.fieldCategoryLabel === fieldType
      );
    }

    // Filter by search term
    if (searchTermAvailable) {
      filteredFields = filteredFields.filter((field) =>
        field.fieldLabel
          .toLowerCase()
          .includes(searchTermAvailable.toLowerCase())
      );
    }

    // Sort alphabetically by label
    filteredFields = filteredFields.sort((a, b) =>
      a.fieldLabel.localeCompare(b.fieldLabel)
    );

    setDisplayedAvailableFields(filteredFields);
  }, [availableFields, selectedOption, searchTermAvailable]);

  useEffect(() => {
    const filteredAndSorted = selectedFields
      .filter((field) =>
        field.fieldLabel
          .toLowerCase()
          .includes(searchTermSelected.toLowerCase())
      )
      .sort((a, b) => a.fieldLabel.localeCompare(b.fieldLabel));
    setDisplayedSelectedFields(filteredAndSorted);
  }, [selectedFields, searchTermSelected]);

  const openPopupHandler = () => {
    setConfirmModal(true);
  };

  const closeModalHandler = () => {
    setConfirmModal(false);
  };
  const nextbtnHandler = () => {
    setEmails('');
    setError('');
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.export.saveAndRun'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.export.savedAndRunAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.export.selectFields'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.export.notifyWhenComplete'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    setOpenEmailPopup(true);
  };

  const modalContent = () => {
    return (
      <>
        <Typography className="typo-text.body2_size rerun-popup-text-color">
          {t('eca.newExport.emailPopupDesc')}
        </Typography>
        <Box sx={{width: '100%', marginTop: '20px'}}>
          <TextField
            value={emails}
            onChange={handleChange}
            fullWidth
            className={`export-email-Popup ${error ? 'error-helper' : ''}`}
            helperText={error || t(exportState?.emailHelperText ?? '')}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            placeholder={t(exportState?.emailPlaceholderText ?? '')}
          />
        </Box>
      </>
    );
  };

  const validateEmails = (emails: any) => {
    const emailArray = emails.trim().split(' ');

    // Regex to validate email format
    const emailRegex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[\w-]{2,4}$/;

    // Check if each email is valid
    for (const email of emailArray) {
      if (!emailRegex.test(email)) {
        return false; // Return false if any email is invalid
      }
    }

    return true; // Return true if all emails are valid
  };

  const fetchTab1Value = useSelector(
    (state: RootState) => state?.addExport?.exportTabsValues
  );

  const handleEmailOkButton = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.continue'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.export.notifyOnEmail'),
      },
      subPage: {
        subpage_name: t('gaTracking.export.notifyWhenComplete'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.export.createNewExport'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    setCreateExportConfirm(true);
    setOpenEmailPopup(false);
  };

  const addExportSubmit = async (exportType: string, reqBody: any) => {
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    try {
      const results = await creatExports(exportType, params, reqBody);
      if (results.meta.status) {
        dispatch(setSelectedExport(true));
        setCreateButtonState(false);
        navigate('/eci-home-page/export-management');
      }
    } catch (e: any) {
      console.log(e);
      setOpenErrorPopup(true);
      setCreateExportConfirm(false);
      setCreateButtonState(false);
      if (e.status === 409) {
        setApiErrorMsg(e?.response?.data?.data?.message);
      } else {
        setApiErrorMsg(
          e?.response?.data?.data?.validationFailures[0]?.message[0]
        );
      }
      setTimeout(() => {
        setOpenErrorPopup(false);
      }, 4000);
    }
  };

  const handleContinueOptionSelected = () => {
    setConfirmModal(false);
    navigate('/eci-home-page/export-management');
  };

  const closeButtonHandler = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.cancel'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.export.notifyOnEmailCancel'),
      },
      subPage: {
        subpage_name: t('gaTracking.export.notifyWhenComplete'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });

    setOpenEmailPopup(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmails(e.target.value);
    const isValid = validateEmails(e.target.value);
    if (isValid) {
      setError('');
      setisButtonDisabled(false);
    } else {
      setError(t('eca.newExport.invalidEmailFormat'));
      setisButtonDisabled(true);
    }
  };

  const createExportConfirmHandler = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.continue'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.export.newExportComplete'),
      },
      subPage: {
        subpage_name: t('gaTracking.export.createNewExport'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });

    setCreateButtonState(true);
    const customFields = selectedFields.map((item) => ({
      fieldId: parseInt(item.fieldId.toString()),
      fieldName: item.fieldName,
      fieldlabel: item.fieldLabel,
    }));
    let mergedObj;
    if (customFields.length > 0) {
      mergedObj = {
        ...fetchTab1Value,
        ...{customFields: customFields},
        ...{notificationEmail: emails},
      };
    } else {
      mergedObj = {
        ...fetchTab1Value,
        ...{notificationEmail: emails},
      };
    }

    const exportType = exportState?.exportType ?? '';
    addExportSubmit(exportType, mergedObj);
  };

  const cancelCreateExportHandler = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.cancel'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.export.newExportCancel'),
      },
      subPage: {
        subpage_name: t('gaTracking.export.createNewExport'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    setCreateExportConfirm(false);
  };

  // Toggle dropdown visibility
  const toggleDropdown = () => setOpen(!open);
  // Function to handle sorting change
  const handleSnackbarClose = () => {
    setOpenErrorPopup(false);
  };

  const isContinueDisabled = () => {
    const canContinue = exportState?.canSaveAndRun(selectedFields) ?? false;
    return !canContinue;
  };

  return (
    <div>
      <div className="columns-section">
        <Box
          className="availabe-fields"
          tabIndex={0}
          aria-label={`${t('accessibility.indexManagement.create.list')} ${t('accessibility.indexManagement.create.listHint')}`}
        >
          <div style={{height: '106px', position: 'relative'}}>
            <Typography className="typo-text subhead4_size text box-sub-heading mist-color-100 padding-left-10">
              {t('eca.newExport.availableField')}
            </Typography>
            <div className="search-filter-container">
              <AurSearchBox
                classNameProps="searchbox-field-filter"
                value={searchTermAvailable ?? ''}
                onSearch={(e) => handleSearchChange(e, 'available')}
              />
              <IconButton
                className="shev-right"
                style={{marginRight: '12px'}}
                ref={buttonRef}
                onClick={toggleDropdown}
                onKeyDown={(event) => {
                  if (event.code === 'Escape') {
                    toggleDropdown();
                  }
                }}
                aria-label={t('common.filters.filterLable')}
              >
                <FilterList />
              </IconButton>
              {open && (
                <div ref={menuRef} className="field-filter-dropdown">
                  {filterOptions.map((item, index) => (
                    <div
                      key={item.id}
                      onClick={() => handleItemSelect(item)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.code === 'Space') {
                          handleItemSelect(item);
                        }
                        if (event.code === 'Escape') {
                          toggleDropdown();
                        }
                      }}
                      className={`dropdown-item ${
                        selectedOption === item.id ? 'selected' : ''
                      }`}
                    >
                      <div
                        className="typo-text body2_size list-items"
                        style={{position: 'relative'}}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.code === 'Space') {
                            handleItemSelect(item);
                          }
                          if (event.code === 'Escape') {
                            toggleDropdown();
                          }
                        }}
                      >
                        {item.display}

                        {selectedOption === item.id && (
                          <CheckedIcon
                            style={{position: 'absolute', right: '0'}}
                          />
                        )}
                      </div>
                      {index === 0 && (
                        <Divider style={{marginTop: '15px'}}></Divider>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Divider></Divider>
          </div>
          <div className="names-list">
            <List role="listbox" aria-label="names-list">
              {displayedAvailableFields.map((field) => (
                <ListItem
                  key={field.fieldId}
                  disablePadding
                  className={`field-text field-export-set ${selectedAvailable.includes(field) ? 'selected' : ''}`}
                  onClick={() => handleToggleAvailable(field)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleToggleAvailable(field);
                    }
                  }}
                  tabIndex={0}
                  role="option"
                  aria-checked={isFieldSelected(field, selectedAvailable)}
                >
                  <div>
                    <span className="typo-text body3_size mist-color-100">
                      {field.fieldLabel}
                    </span>
                    <span
                      className="typo-text body3_size mist-color-700"
                      style={{marginLeft: '5px'}}
                    >
                      ({field.fieldCategoryLabel})
                    </span>
                  </div>
                  {isFieldSelected(field, selectedAvailable) && <CheckedIcon />}
                </ListItem>
              ))}
            </List>
          </div>
        </Box>

        <div className="arrow-control">
          <div className="left-to-right">
            <IconButton
              aria-disabled={
                selectedAvailable.length === 0 || selectedAvailable.length === 1
              }
              onClick={(e) => {
                if (
                  selectedAvailable.length === 0 ||
                  selectedAvailable.length === 1
                ) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                moveToSelected();
              }}
              className={`shev-right ${(selectedAvailable.length === 0 || selectedAvailable.length === 1) && 'arrow-icon-disabled'}`}
              aria-label={t('ecaDocList.filterOptions.addAllFields')}
              data-testid="btn-move-all-right"
            >
              {selectedAvailable.length === 0 ||
              selectedAvailable.length === 1 ? (
                <ArrowDoubbleRightInactive />
              ) : (
                <ArrowDoubbleRight />
              )}
            </IconButton>
            <IconButton
              aria-disabled={
                selectedAvailable.length === 0 || selectedAvailable.length > 1
              }
              onClick={(e) => {
                if (
                  selectedAvailable.length === 0 ||
                  selectedAvailable.length > 1
                ) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                moveToSelected();
              }}
              className={`shev-right ${(selectedAvailable.length === 0 || selectedAvailable.length > 1) && 'arrow-icon-disabled'}`}
              aria-label={t('ecaDocList.filterOptions.addSelectedFields')}
              data-testid="btn-move-single-right"
            >
              {selectedAvailable.length === 0 ||
              selectedAvailable.length > 1 ? (
                <ArrowRightOutlinedIcon />
              ) : (
                <ArrowRightOutlinedIconActive />
              )}
            </IconButton>
          </div>
          <Divider></Divider>
          <div className="left-to-right">
            <IconButton
              aria-disabled={
                selectedSelected.length === 0 || selectedSelected.length > 1
              }
              onClick={(e) => {
                if (
                  selectedSelected.length === 0 ||
                  selectedSelected.length > 1
                ) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                moveToAvailable();
              }}
              className={`shev-right ${(selectedSelected.length === 0 || selectedSelected.length > 1) && 'arrow-icon-disabled'}`}
              aria-label={t('ecaDocList.filterOptions.removeSelectedFields')}
              data-testid="btn-move-single-left"
            >
              {selectedSelected.length === 0 || selectedSelected.length > 1 ? (
                <ArrowLeftOutlinedIcon />
              ) : (
                <ArrowLeftOutlinedIconActive />
              )}
            </IconButton>
            <IconButton
              aria-disabled={
                selectedSelected.length === 0 || selectedSelected.length === 1
              }
              onClick={(e) => {
                if (
                  selectedSelected.length === 0 ||
                  selectedSelected.length === 1
                ) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                moveToAvailable();
              }}
              className={`shev-right ${(selectedSelected.length === 0 || selectedSelected.length === 1) && 'arrow-icon-disabled'}`}
              aria-label={t('ecaDocList.filterOptions.removeAllFields')}
              data-testid="btn-move-all-left"
            >
              {selectedSelected.length === 0 ||
              selectedSelected.length === 1 ? (
                <ArrowDoubbleLeft />
              ) : (
                <ArrowDoubbleLeftActive />
              )}
            </IconButton>
          </div>
        </div>

        <Box
          className="availabe-fields"
          tabIndex={0}
          aria-label={t('eca.newExport.selectedField')}
        >
          <div style={{height: '106px'}}>
            <Typography className="typo-text subhead4_size text mist-color-100 box-sub-heading padding-left-10">
              {t('eca.newExport.selectedField')}
            </Typography>
            <AurSearchBox
              classNameProps="searchbox-field"
              value={searchTermSelected ?? ''}
              onSearch={(e) => handleSearchChange(e, 'selected')}
            />
            <Divider></Divider>
          </div>
          <div className="names-list">
            <List role="listbox" aria-label="selected-tags">
              <ReactSortable
                list={displayedSelectedFields}
                setList={setDisplayedSelectedFields}
                className="sortable-list"
                style={{listStyle: 'none', padding: 0}}
              >
                {displayedSelectedFields.map((field) => (
                  <ListItem
                    key={field.fieldId}
                    disablePadding
                    className={`field-text field-export-set ${selectedSelected.includes(field) ? 'selected' : ''}`}
                    onClick={() => handleToggleSelected(field)}
                    onKeyDown={(e) => {
                      const index = displayedSelectedFields.findIndex(
                        (f) => f.fieldId === field.fieldId
                      );
                      const newList = [...displayedSelectedFields];

                      if (e.key === 'ArrowUp' && index > 0) {
                        [newList[index - 1], newList[index]] = [
                          newList[index],
                          newList[index - 1],
                        ];
                        setDisplayedSelectedFields(newList);
                        e.preventDefault();
                      }

                      if (e.key === 'ArrowDown' && index < newList.length - 1) {
                        [newList[index + 1], newList[index]] = [
                          newList[index],
                          newList[index + 1],
                        ];
                        setDisplayedSelectedFields(newList);
                        e.preventDefault();
                      }
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleToggleSelected(field);
                      }
                    }}
                    tabIndex={0}
                    role="option"
                    aria-checked={selectedSelected.includes(field)}
                  >
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <span style={{marginRight: '15px', cursor: 'move'}}>
                        <DragAndDrop />
                      </span>
                      <span className="body3_size mist-color-500">
                        {field.fieldLabel}
                      </span>
                      <span
                        className="body3_size mist-color-700"
                        style={{marginLeft: '5px'}}
                      >
                        ({field.fieldCategoryLabel})
                      </span>
                    </div>
                  </ListItem>
                ))}
              </ReactSortable>
            </List>
          </div>
        </Box>
      </div>
      <Box className="index-action-container">
        <div className="custom-index-action-button">
          <AurButton
            type="exit"
            label={t('buttons.cancel')}
            title={t('buttons.cancel')}
            size="Medium"
            onClick={() => openPopupHandler()}
            ariaDescription={t('eca.newExport.cancelHint')}
          />
          <AurButton
            type="continue"
            label={t('eca.newExport.saveRun')}
            title={t('eca.newExport.saveRun')}
            size="Medium"
            onClick={nextbtnHandler}
            dataTestId={'closeBtn-test'}
            disabled={isContinueDisabled()}
            ariaDescription={t('eca.newExport.saveHint')}
          />
        </div>
      </Box>
      {confirmModal && (
        <div>
          <AurPopup
            className="aur-unsaved-changes-popup export-cancel-popup"
            open={confirmModal}
            onOk={closeModalHandler}
            onCancel={handleContinueOptionSelected}
            popupHeading={t('eca.newExport.expoCancelPopupHeading')}
            showPopupHeading={true}
            popupTitle={t('common.clearAllPopup.heading')}
            showPopupTitle={true}
            showCloseIcon={false}
            popupContent={t('eca.newExport.expoCancelPopupContent')}
            labelActionCancel={t('eca.newExport.discardChangeBtn')}
            labelActionOk={t('eca.newExport.stayOnPageBtn')}
            exitButtonHint={t('eca.newExport.cancelHint')}
            continueButtonHint={t('common.clearAllPopup.confirmToClearHint')}
            cancelButtonTitle=""
          />
        </div>
      )}

      {openEmailPopup && (
        <AurPopup
          className="aur-unsaved-changes-popup index-popup cacel-btn-transparent"
          open={openEmailPopup}
          onOk={handleEmailOkButton}
          onCancel={closeButtonHandler}
          showPopupHeading={true}
          popupHeading={t(exportState?.notificationText ?? '')}
          popupTitle={''}
          popupContent={modalContent()}
          contentType="element"
          showPopupTitle={true}
          showCloseIcon={true}
          labelActionCancel={t('common.clearAllPopup.buttonCancel')}
          labelActionOk={t('indexes.reRunPopup.continueBtn')}
          exitButtonHint={t('eca.newExport.cancel2Hint')}
          continueButtonHint={t('eca.newExport.continueHint')}
          cancelButtonTitle=""
          continueButtonState={isButtonDisabled}
          closeIconHint={t('eca.newExport.cancel2Hint')}
        />
      )}
      {createExportConfirm && (
        <div className="export-confirm-popup">
          <AurPopup
            className="aur-unsaved-changes-popup export-confirm-popup"
            open={createExportConfirm}
            onOk={createExportConfirmHandler}
            onCancel={cancelCreateExportHandler}
            popupHeading={t('eca.newExport.exportPopup')}
            showPopupHeading={true}
            showPopupTitle={true}
            popupTitle={''}
            showCloseIcon={false}
            popupContentClass="typo-text body2_size"
            popupContent={t('eca.newExport.exportConfirmDesc')}
            labelActionCancel={t('common.clearAllPopup.buttonCancel')}
            labelActionOk="Continue"
            exitButtonHint={t('eca.newExport.cancel2Hint')}
            continueButtonHint={t('eca.newExport.continueHint')}
            cancelButtonTitle=""
            continueButtonState={createButtonState}
          />
        </div>
      )}
      <div className="create-export-toast">
        <AurToast
          ariaLabel={t('common.toastAlert.toastAlertHint')}
          hideDuration={5000}
          open={openErrorPopup}
          severity={'error'}
          horizontalPosition={'center'}
          verticalPosition={'bottom'}
          message={apiErrorMsg}
          onClose={() => handleSnackbarClose}
        />
      </div>
    </div>
  );
};

export default CreateExportStep2;
